import { SearchResultPageLink } from '@app/data/clients/rbServerSideClient/utils/buildShopPagination';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import ChevronLeftIcon from '@redbubble/design-system/src/Icons/ChevronLeft';
import ChevronLeftBigIcon from '@redbubble/design-system/src/Icons/ChevronLeftBig';
import ChevronRightIcon from '@redbubble/design-system/src/Icons/ChevronRight';
import ChevronRightBigIcon from '@redbubble/design-system/src/Icons/ChevronRightBig';
import ContextHorizontalIcon from '@redbubble/design-system/src/Icons/ContextHorizontal';
import Text from '@redbubble/design-system/src/Text';
import { useIntl } from '@redbubble/next/intl';
import { messages } from './messages';
import styles from './Pagination.module.scss';

interface PaginationProps {
  firstGap: boolean;
  middlePages: SearchResultPageLink[];
  lastGap: boolean;
  firstPage?: SearchResultPageLink;
  lastPage?: SearchResultPageLink;
  nextPage?: SearchResultPageLink;
  previousPage?: SearchResultPageLink;
  handlePagination: (url: string) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  firstGap,
  middlePages,
  lastGap,
  firstPage,
  lastPage,
  nextPage,
  previousPage,
  handlePagination,
}) => {
  const intl = useIntl();

  return (
    <Box
      className={styles.paginationContainer}
      aria-label={intl.formatMessage(messages.pagNav)}
    >
      {previousPage ? (
        <>
          <div className={[styles.previousButton, styles.mobile].join(' ')}>
            <Button
              href={previousPage.link}
              onClick={(e) => {
                e.preventDefault();
                handlePagination(previousPage.link);
              }}
              iconBefore={<ChevronLeftBigIcon size={constants.MEDIUM} />}
              circle
              aria-label={intl.formatMessage(messages.previousPage)}
            />
          </div>
          <a
            href={previousPage.link}
            className={[styles.previousButton, styles.desktop].join(' ')}
            onClick={(e) => {
              e.preventDefault();
              handlePagination(previousPage.link);
            }}
            aria-label={intl.formatMessage(messages.previousPage)}
          >
            <ChevronLeftIcon size={constants.SMALL} />
            <Text className={styles.previousButtonText} type="body2">
              {intl.formatMessage(messages.previous)}
            </Text>
          </a>
        </>
      ) : (
        <div
          className={[
            styles.previousButton,
            styles.desktop,
            styles.disabled,
          ].join(' ')}
        >
          <ChevronLeftIcon size={constants.SMALL} />
          <Text className={styles.previousButtonText} type="body2">
            {intl.formatMessage(messages.previous)}
          </Text>
        </div>
      )}
      <div className={styles.buttonContainer}>
        {firstPage && (
          <a
            href={firstPage.link}
            className={[
              styles.paginationButton,
              firstPage.active && styles.active,
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={(e) => {
              e.preventDefault();
              handlePagination(firstPage.link);
            }}
            aria-current={firstPage.active ? 'true' : undefined}
            aria-label={intl.formatMessage(messages.goToPage, {
              pageNumber: firstPage.text,
            })}
          >
            <Text className={styles.paginationText}>{firstPage.text}</Text>
          </a>
        )}
        {firstGap && (
          <span
            className={styles.paginationGap}
            aria-hidden="true"
            role="presentation"
          >
            <ContextHorizontalIcon size={constants.SMALL} />
          </span>
        )}
        {middlePages.map((page) => (
          <a
            key={page.text}
            href={page.link}
            className={[styles.paginationButton, page.active && styles.active]
              .filter(Boolean)
              .join(' ')}
            onClick={(e) => {
              e.preventDefault();
              handlePagination(page.link);
            }}
            aria-current={page.active ? 'true' : undefined}
            aria-label={intl.formatMessage(messages.goToPage, {
              pageNumber: page.text,
            })}
          >
            <Text className={styles.paginationText}>{page.text}</Text>
          </a>
        ))}
        {lastGap && (
          <span
            className={styles.paginationGap}
            aria-hidden="true"
            role="presentation"
          >
            <ContextHorizontalIcon size={constants.SMALL} />
          </span>
        )}
        {lastPage && (
          <a
            href={lastPage.link}
            className={[
              styles.paginationButton,
              lastPage.active && styles.active,
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={(e) => {
              e.preventDefault();
              handlePagination(lastPage.link);
            }}
            aria-current={lastPage.active ? 'page' : undefined}
            aria-label={intl.formatMessage(messages.goToPage, {
              pageNumber: lastPage.text,
            })}
          >
            <Text className={styles.paginationText}>{lastPage.text}</Text>
          </a>
        )}
      </div>
      {nextPage ? (
        <>
          <div className={[styles.nextButton, styles.mobile].join(' ')}>
            <Button
              href={nextPage.link}
              onClick={(e) => {
                e.preventDefault();
                handlePagination(nextPage.link);
              }}
              iconBefore={<ChevronRightBigIcon size={constants.MEDIUM} />}
              circle
              aria-label={intl.formatMessage(messages.nextPage)}
            />
          </div>
          <a
            href={nextPage.link}
            className={[styles.nextButton, styles.desktop].join(' ')}
            onClick={(e) => {
              e.preventDefault();
              handlePagination(nextPage.link);
            }}
            aria-label={intl.formatMessage(messages.nextPage)}
          >
            <Text className={styles.nextButtonText} type="body2">
              {intl.formatMessage(messages.next)}
            </Text>
            <ChevronRightIcon size={constants.SMALL} />
          </a>
        </>
      ) : (
        <div
          className={[styles.nextButton, styles.desktop, styles.disabled].join(
            ' '
          )}
        >
          <Text className={styles.nextButtonText} type="body2">
            {intl.formatMessage(messages.next)}
          </Text>
          <ChevronRightIcon size={constants.SMALL} />
        </div>
      )}
    </Box>
  );
};
